import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { DEFAULT_SCORING_CONFIG } from '../config/scoringConfig';

const ScoringConfigContext = createContext(null);

const STORAGE_KEY = 'skiing_scoring_config_v5';

/**
 * Deep merge helper - merges source into target
 */
function deepMerge(target, source) {
  const result = { ...target };
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
}

/**
 * Provider component for scoring configuration
 * Manages global scoring config state with localStorage persistence
 */
export function ScoringConfigProvider({ children }) {
  const [config, setConfig] = useState(() => {
    // Load from localStorage if available
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          // Merge with defaults to handle any new fields added in updates
          return deepMerge(DEFAULT_SCORING_CONFIG, parsed);
        } catch (e) {
          console.warn('Failed to parse stored scoring config:', e);
        }
      }
    }
    return DEFAULT_SCORING_CONFIG;
  });

  // Persist to localStorage whenever config changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    }
  }, [config]);

  /**
   * Update weight for a specific metric in a specific mode
   */
  const updateMetricWeight = useCallback((mode, metricId, weight) => {
    setConfig(prev => ({
      ...prev,
      weights: {
        ...prev.weights,
        [mode]: {
          ...prev.weights[mode],
          [metricId]: Math.max(0, Math.min(1, weight))
        }
      }
    }));
  }, []);

  /**
   * Update sweet spot range for a metric
   */
  const updateMetricSweetSpot = useCallback((metricId, sweetSpot) => {
    setConfig(prev => ({
      ...prev,
      metrics: {
        ...prev.metrics,
        [metricId]: {
          ...prev.metrics[metricId],
          sweetSpot: {
            min: Math.min(sweetSpot.min, sweetSpot.max),
            max: Math.max(sweetSpot.min, sweetSpot.max)
          }
        }
      }
    }));
  }, []);

  /**
   * Update the display range for a metric
   */
  const updateMetricRange = useCallback((metricId, range) => {
    setConfig(prev => ({
      ...prev,
      metrics: {
        ...prev.metrics,
        [metricId]: {
          ...prev.metrics[metricId],
          range: {
            min: Math.min(range.min, range.max),
            max: Math.max(range.min, range.max)
          }
        }
      }
    }));
  }, []);

  /**
   * Update the behavior type for a metric (sweet_spot, more_is_better, less_is_better)
   */
  const updateMetricBehavior = useCallback((metricId, behavior) => {
    setConfig(prev => ({
      ...prev,
      metrics: {
        ...prev.metrics,
        [metricId]: {
          ...prev.metrics[metricId],
          behavior
        }
      }
    }));
  }, []);

  /**
   * Update a scoring threshold for a metric
   */
  const updateScoringThreshold = useCallback((metricId, index, threshold, score) => {
    setConfig(prev => {
      const metric = prev.metrics[metricId];
      if (!metric) return prev;

      const newScoring = [...metric.scoring];
      newScoring[index] = {
        ...newScoring[index],
        threshold: threshold !== undefined ? threshold : newScoring[index].threshold,
        score: score !== undefined ? score : newScoring[index].score
      };

      return {
        ...prev,
        metrics: {
          ...prev.metrics,
          [metricId]: {
            ...metric,
            scoring: newScoring
          }
        }
      };
    });
  }, []);

  /**
   * Reset to default configuration
   */
  const resetToDefaults = useCallback(() => {
    setConfig(DEFAULT_SCORING_CONFIG);
  }, []);

  /**
   * Normalize weights to sum to 1.0
   */
  const normalizeWeights = useCallback((mode) => {
    setConfig(prev => {
      const weights = prev.weights[mode];
      const total = Object.values(weights).reduce((sum, w) => sum + w, 0);
      if (total === 0) return prev;

      const normalized = {};
      Object.entries(weights).forEach(([key, val]) => {
        normalized[key] = val / total;
      });

      return {
        ...prev,
        weights: {
          ...prev.weights,
          [mode]: normalized
        }
      };
    });
  }, []);

  /**
   * Update a hard cap
   */
  const updateHardCap = useCallback((mode, index, updates) => {
    setConfig(prev => {
      const caps = [...prev.hardCaps[mode]];
      caps[index] = { ...caps[index], ...updates };
      return {
        ...prev,
        hardCaps: {
          ...prev.hardCaps,
          [mode]: caps
        }
      };
    });
  }, []);

  /**
   * Update fresh snow requirements
   */
  const updateFreshSnowRequirements = useCallback((mode, updates) => {
    setConfig(prev => ({
      ...prev,
      freshSnowRequirements: {
        ...prev.freshSnowRequirements,
        [mode]: {
          ...prev.freshSnowRequirements[mode],
          ...updates
        }
      }
    }));
  }, []);

  /**
   * Update zone logic thresholds
   */
  const updateZoneLogic = useCallback((zone, updates) => {
    setConfig(prev => ({
      ...prev,
      zoneLogic: {
        ...prev.zoneLogic,
        [zone]: {
          ...prev.zoneLogic[zone],
          ...updates
        }
      }
    }));
  }, []);

  const value = {
    config,
    setConfig,
    updateMetricWeight,
    updateMetricSweetSpot,
    updateMetricRange,
    updateMetricBehavior,
    updateScoringThreshold,
    resetToDefaults,
    normalizeWeights,
    updateHardCap,
    updateFreshSnowRequirements,
    updateZoneLogic
  };

  return (
    <ScoringConfigContext.Provider value={value}>
      {children}
    </ScoringConfigContext.Provider>
  );
}

/**
 * Hook to access scoring configuration context
 * @returns {Object} Scoring config context value
 */
export function useScoringConfig() {
  const context = useContext(ScoringConfigContext);
  if (!context) {
    // Return default config if used outside provider (for backward compatibility)
    return {
      config: DEFAULT_SCORING_CONFIG,
      setConfig: () => {},
      updateMetricWeight: () => {},
      updateMetricSweetSpot: () => {},
      updateMetricRange: () => {},
      updateMetricBehavior: () => {},
      updateScoringThreshold: () => {},
      resetToDefaults: () => {},
      normalizeWeights: () => {},
      updateHardCap: () => {},
      updateFreshSnowRequirements: () => {},
      updateZoneLogic: () => {}
    };
  }
  return context;
}

export default ScoringConfigContext;
